import { apolloClient, apolloClientHolder, FieldDescriptor, PrivateRoute, Utils } from "@crispico/foundation-react";
import { Reducers, ReduxReusableComponents, RRCProps, State } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import React from "react";
import lodash from "lodash";
import { Input, Radio, Segment, TextArea, Form, FormField } from "semantic-ui-react";
import gql from "graphql-tag";
import { FindByFilterParams } from "@crispico/foundation-react/entity_crud/FindByFilterParams";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { FilterOperators } from "@crispico/foundation-gwt-js";
import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import _ from "lodash";
import { Severity } from "@crispico/foundation-react/components/ModalExt/ModalExt";
import moment from "moment";
import { MISSION_ASSIGN_OBJECT_TO_MISSION, MISSION_DELETE, MISSION_SAVE, OAG_DELETE } from "./queries";
import { HUMAN_RESOURCE_GET_QUALIFICATIONS_FOR_INTERVAL } from "pages/HumanResourceSchedule/queries";
import { AssociationFieldEditor } from "@crispico/foundation-react/entity_crud/fieldEditors/AssociationFieldEditor";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import { XopsInitializationsForClient } from "app";
import { WizardRRC, WizardStep } from "@crispico/foundation-react/components/Wizard/Wizard";
import { flightEntityDescriptor, taskEntityDescriptor } from "AppEntityDescriptors";
import { GanttUtils } from "./GanttUtils";
import { MissionValidation, MissionValidationRRC } from "pages/Mission2/MissionValidation";

const MAP_PRIORITY: { [key: string]: number } = { "P": 0, "R": 1, "1R": 2, "2R": 3, "RTL": 4, "S": 5, "T": 6, "1T": 7, "2T": 8, "L": 9, "H": 10 };
const POSITION_AR = "AR";
const POSITION_AV = "AV";

export interface AssignTasksToResourcePageSettings {
    maxTasksToAssign: number;
    verifyEquipmentTypeCapacity: boolean;
    showTemporaryStorageInfo: boolean;
    showEquipmentTypeCapacityInfo: boolean;
    showEquipmentTypeInfo: boolean;
    showEquipmentResourceInfo: boolean;
    showAssignUnitInfo: boolean;
    sortTasksBasedOnFields: string;
    selectTaskWithoutEquipmentType: boolean;
    selectExistingMission: boolean;
    checkAddressesFields: boolean;
    checkQualification: boolean;
    maxTasksForMission: number;
    checkNullOilGroup: boolean;
}

export enum MissionStatusGroup { NEW, EXISTING, NEW_UNIT }
export enum TemporaryStorageGroup { FROM, TO }

export type AssignTasksToResourcePageProps = {
    degradedMode?: boolean;
    taskIds?: number[];
    initialSelectedTaskIds?: number[];
    missionId?: number;
    // Required if mission is undefined.
    equipmentResourceId?: number;
    // Required if mission is undefined.
    humanResourceId?: number;
    equipmentTypeTaskIds?: any[];
    temporaryStorage?: string; // code of address
    createTruckObjectsForFlightUid?: string; // default null
    onAssign?: (uid: string) => void;
    onError?: () => void;
    onCancel?: () => void;
    missionCreationDate?: string; // (GanttWrapperRoots.INSTANCE.isTemplateDay || GanttWrapperRoots.INSTANCE.isFutureDay) ? GanttWrapperRoots.INSTANCE.dateBeginningOfDay : null
    defaultMissionStatusGroup?: MissionStatusGroup;
    hideTaskSelection?: boolean;
}

export class AssignTasksToResourcePageState extends State {
    settings: AssignTasksToResourcePageSettings | undefined;
    flights: any[] = [];
    allTasks: any[] = [];
    tasks: any[] = [];
    mission?: any;
    equipmentResource?: any;
    humanResource?: any;
    equipmentTypeTasks: any[] = [];
    missionStatusGroup = MissionStatusGroup.NEW;
    enableNewUnitMission = true;
    equipmentType?: any;
    attachment?: any;
    temporaryStorage?: any;
    comment?: string;
    temporaryStorageGroup = TemporaryStorageGroup.TO;
    taskTypes: { [key: string]: any } = {};
    alerts: string[] = [];
    numberOfMissionsToBeCreated?: number;
    savedMission?: any;
}

export class AssignTasksToResourcePageReducers<S extends AssignTasksToResourcePageState = AssignTasksToResourcePageState> extends Reducers<S> {
    clearSavedMission() {
        this.s.savedMission = undefined;
    }
}

type Props = RRCProps<AssignTasksToResourcePageState, AssignTasksToResourcePageReducers> & AssignTasksToResourcePageProps;

function filterById(ids: number[]) { return Filter.create("id", FilterOperators.forNumber.in, ids.join(", ")) };

export class AssignTasksToResourcePage extends React.Component<Props> {

    static defaultProps = {
        degradedMode: false
    }
    
    missionTypes: { [key: string]: any } = {};
    baggagesAlertMap: { [key: string]: any } = {};
    baggagesAlertMapMaxPrefixLength: number = -1;
    baggagesAlertMapMinPrefixLength: number = Number.MAX_VALUE;
    
    private missionValidationRef = React.createRef<MissionValidation>();

    constructor(props: Props) {
        super(props);
    }

    async componentDidMount() {
        await this.getSetting();
        if (this.props.temporaryStorage) {
            const temporaryStorage = (await this.loadEntities("Address", Filter.create("code", FilterOperators.forString.equals, this.props.temporaryStorage)))[0];
            this.props.r.setInReduxState({ temporaryStorage });
        }
        await this.getHumanResource();
        await this.getEquipmentResource();
        await this.getMission();
        await this.getTasks();
        await this.getMissionTypes();
        await this.getAttachment();
        await this.getBaggagesAlert();
        await this.getEquipmentTypeTasks();
        if (this.props.s.settings?.selectExistingMission) {
            this.handleMissionStatusGroup(MissionStatusGroup.EXISTING);
        } else if (this.props.defaultMissionStatusGroup) {
            this.handleMissionStatusGroup(this.props.defaultMissionStatusGroup);
        }
    }

    async componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.s.equipmentType != prevProps.s.equipmentType) {
            await this.getEquipmentTypeTasks(!this.props.s.equipmentType);
        }
        if (this.props.s.temporaryStorage && this.props.s.temporaryStorage != prevProps.s.temporaryStorage) {
            this.filterTasks();
        }
        if (this.props.s.temporaryStorageGroup != prevProps.s.temporaryStorageGroup) {
            this.filterTasks();
        }
    }

    private async getSetting() {
        const settings = (await apolloClientHolder.apolloClient.query({
            query: gql(`query q { 
                mission2Service_assignTasksToResourcePageSettings {
                    maxTasksToAssign verifyEquipmentTypeCapacity showTemporaryStorageInfo
                    showEquipmentTypeCapacityInfo showEquipmentTypeInfo showEquipmentResourceInfo
                    showAssignUnitInfo sortTasksBasedOnFields selectTaskWithoutEquipmentType
                    selectExistingMission checkAddressesFields maxTasksForMission checkNullOilGroup checkQualification
                }
            }`)
        })).data["mission2Service_assignTasksToResourcePageSettings"];
        this.props.r.setInReduxState({ settings })
    }

    private async loadEntities(entityName: string, filter?: Filter) {
        const ed = entityDescriptors[entityName];
        const loadOperationName = `${lodash.lowerFirst(entityName)}Service_findByFilter`;
        let fieldsToRequestStr = ed.getGraphQlFieldsToRequest();
        if (entityName === "Mission2") {
            fieldsToRequestStr += " stops { objectActions { dataString type } } inactivityType { id isPartial hideInGantt } tasks { id } ";
        }
        if (entityName === "Task") {
            fieldsToRequestStr += " taskGroup { id name oilGroup { id } destination airline date number tasks { id mission { id } } } taskType { id name requiredQualifications { id qualificationType { id name } } } mission { id } ";
        }
        if (entityName === "HumanResource") {
            fieldsToRequestStr += " vehicle { id attachmentId } qualifications { id qualificationType { id name } }";
        }
        if (entityName === "BaggagesAlert") {
            fieldsToRequestStr += " objectType { id prefix }";
        }
        if (entityName === "EquipmentTypeTask") {
            fieldsToRequestStr += " taskType { id name ocupiedPositions }";
        }

        const loadQueryParams = {
            loadOperationName,
            loadQuery: gql(`query q($params: FindByFilterParamsInput) { 
                ${loadOperationName}(params: $params) {
                    results { ${fieldsToRequestStr} } totalCount
                }
            }`)
        };
        const entities = (await apolloClientHolder.apolloClient.query({
            query: loadQueryParams.loadQuery,
            variables: FindByFilterParams.create().filter(filter ? Filter.createComposed(FilterOperators.forComposedFilter.and, [filter]) : undefined)
        })).data[loadQueryParams.loadOperationName].results;
        return entities;
    }

    private async getBaggagesAlert() {
        const baggagesAlert = await this.loadEntities("BaggagesAlert");
        baggagesAlert.forEach((bagAl: any) => {
            const prefix = (bagAl.objectType == null) ? "" : bagAl.objectType.prefix;
            const dest = (bagAl.flightDestination == null) ? "" : bagAl.flightDestination;
            const prefixAndDestinationAsKey = prefix + "-" + dest;
            this.baggagesAlertMap[prefixAndDestinationAsKey] = bagAl.maxNumber;
            if (prefix.length > this.baggagesAlertMapMaxPrefixLength) {
                this.baggagesAlertMapMaxPrefixLength = prefix.length;
            }
            if (prefix.length < this.baggagesAlertMapMinPrefixLength) {
                this.baggagesAlertMapMinPrefixLength = prefix.length;
            }
        })
    }

    private async getMission() {
        const mission = this.props.missionId ? (await this.loadEntities("Mission2", filterById([this.props.missionId])))?.[0] : undefined;
        this.props.r.setInReduxState({ mission });
    }

    private async getAttachment() {
        const attachment = this.props.s.humanResource?.vehicle?.attachmentId ? (await this.loadEntities("EquipmentResource", filterById(this.props.s.humanResource.vehicle.attachmentId)))?.[0] : undefined;
        this.props.r.setInReduxState({ attachment });
    }

    private async getEquipmentResource(id?: number) {
        // used id = 0, in prod the id > 0, and on test id < 0
        const equipmentResource = (this.props.equipmentResourceId || id) ? (await this.loadEntities("EquipmentResource", filterById([id ? id : this.props.equipmentResourceId || 0])))?.[0] : undefined;
        this.props.r.setInReduxState({ equipmentResource, equipmentType: equipmentResource?.equipmentType });
    }

    private async getHumanResource() {
        const humanResource = this.props.humanResourceId ? (await this.loadEntities("HumanResource", filterById([this.props.humanResourceId])))?.[0] : undefined;
        this.props.r.setInReduxState({ humanResource });
    }

    private async getEquipmentTypeTasks(initial: boolean = true) {
        const filter = initial && this.props.equipmentTypeTaskIds ? filterById(this.props.equipmentTypeTaskIds) : (this.props.s.equipmentType ? Filter.create("equipmentType.id", FilterOperators.forNumber.equals, this.props.s.equipmentType.id) : undefined);
        const equipmentTypeTasks = (await this.loadEntities("EquipmentTypeTask", filter));
        const taskTypeIds = equipmentTypeTasks.map((eQTT: any) => { return eQTT.taskType.id }) as number[];
        const taskTypes = taskTypeIds.length ? (await this.loadEntities("TaskType", filterById(taskTypeIds))) : [];

        this.props.r.setInReduxState({ equipmentTypeTasks, taskTypes });
    }

    private async getTasks() {
        if (!this.props.taskIds?.length) {
            return;
        }
        const allTasks: any[] = await this.loadEntities("Task", filterById(this.props.taskIds));
        let missionStatusGroup = this.props.s.missionStatusGroup;
        if (this.props.s.settings?.showAssignUnitInfo && this.props.taskIds && _.isEqual(this.props.taskIds, this.props.initialSelectedTaskIds)) {
            missionStatusGroup = MissionStatusGroup.NEW_UNIT;
        }
        const uniqueFlights: { [key: string]: any } = {};
        allTasks.forEach((task: any) => uniqueFlights[task.taskGroup.id] = task.taskGroup);
        this.props.r.setInReduxState({ allTasks, missionStatusGroup, flights: Array.from(Object.values(uniqueFlights)) });
        this.filterTasks();
    }

    private async getMissionTypes() {
        const results = await this.loadEntities("MissionType");
        this.missionTypes = {};
        results.forEach((missionType: any) => {
            this.missionTypes[missionType.id] = missionType;
        })
    }

    sortFunction(a: any, b: any) {
        const fields = this.props.s.settings?.sortTasksBasedOnFields.split(" ") || [];
        for (let i = 0; i < fields.length; i++) {
            let valA = _.cloneDeep(a), valB = _.cloneDeep(b);
            let composedFileds = fields[i].split(".");
            let nullValueString = "", nullValueInt = Number.MIN_VALUE;
            if (fields[i] === "position") {
                // vea: position is sorted by number [2..], after by type [0:2]
                composedFileds = ["position1", "position2"];
                valA["position1"] = valA["position"] ? valA["position"].substring(2) : undefined;
                valB["position1"] = valB["position"] ? valB["position"].substring(2) : undefined;
                const aPos = valA["position"] ? valA["position"].substring(0, 2) : undefined,
                    bPos = valB["position"] ? valB["position"].substring(0, 2) : undefined;
                valA["position2"] = aPos === POSITION_AR ? 0 : (aPos === POSITION_AV ? 1 : -1);
                valB["position2"] = bPos === POSITION_AR ? 0 : (bPos === POSITION_AV ? 1 : -1);
            }
            for (let j = 0; j < composedFileds.length; j++) {
                if ((composedFileds.length === 2 && composedFileds[0] === "missionType" && composedFileds[1] === "name") ||
                    composedFileds[0] === "position1" || composedFileds[0] === "position2" || composedFileds[0] === "priority") {
                    // onet: missionType.name, priority on sort put first null
                    // vea: position, on sort put first null
                    nullValueString = "Z";
                    nullValueInt = Number.MAX_VALUE;
                }
                if (composedFileds[0] === "priority") {
                    // onet: map priority
                    valA["priority"] = MAP_PRIORITY[valA["priority"]];
                    valB["priority"] = MAP_PRIORITY[valB["priority"]];
                }
                valA = valA[composedFileds[j]] ? valA[composedFileds[j]] : (typeof valA[composedFileds[j]] == "string" ? nullValueString : nullValueInt);
                valB = valB[composedFileds[j]] ? valB[composedFileds[j]] : (typeof valB[composedFileds[j]] == "string" ? nullValueString : nullValueInt);
                if (!valA[composedFileds[j]] || !valA[composedFileds[j]]) {
                    break;
                }
            }
            if (valA === valB) {
                continue;
            } else {
                if (typeof valB == "string") {
                    // compare strings caseInsensitive
                    valA = valA.toLocaleLowerCase(); valB = valB.toLocaleLowerCase();
                    return valA.localeCompare(valB);
                }
                return valA - valB;
            }
        }
        return 0;
    }

    filterTasks() {
        const alerts: any[] = [];
        let tasks = this.props.s.allTasks.filter((task: any) => {
            if (task.archived || task.mission || (this.props.s.temporaryStorage && ((this.props.s.temporaryStorageGroup === TemporaryStorageGroup.FROM && task.startAddress?.id != this.props.s.temporaryStorage.id)
                || (this.props.s.temporaryStorageGroup === TemporaryStorageGroup.TO && task.endAddress?.id != this.props.s.temporaryStorage.id)))) {
                return false;
            }
            return true;
        })
        let enableNewUnitMission = true;
        tasks.forEach(async (task: any) => {
            if (this.props.s.settings?.checkQualification) {
                if (this.props.s.humanResource && task.taskType.requiredQualifications?.length) {
                    let qualifications = (await apolloClientHolder.apolloClient.query({
                        query: HUMAN_RESOURCE_GET_QUALIFICATIONS_FOR_INTERVAL, variables: {
                            startDate: moment(task.taskGroup.date).startOf("day"),
                            endDate: moment(task.taskGroup.date).endOf("day"),
                            humanResourcesIds: [this.props.s.humanResource.id]
                        }, context: { showSpinner: false }
                    })).data["humanResourceService_qualificationsForDate"];

                    task.taskType.requiredQualifications.forEach((requiredQualification: any) => {
                        let isQualified = false;
                        qualifications.forEach((qualification: any) => {
                            if (qualification.qualificationType.name === requiredQualification.qualificationType.name) {
                                isQualified = true;
                            }
                        })
                        if (!isQualified) {
                            alerts.push(_msg("assignObjects.notQualified", this.props.s.humanResource.lastName + " " + this.props.s.humanResource.firstName, requiredQualification.qualificationType.name))
                        }
                    })
                }
            }
            // if already found a flight without any mission no need to check other tasks/flights
            if (enableNewUnitMission) {
                let foundMissionForFlight = false;
                task.taskGroup.tasks.forEach((task: any) => {
                    if (task.mission) {
                        foundMissionForFlight = true;
                        return;
                    }
                })
                if (!foundMissionForFlight) {
                    enableNewUnitMission = false;
                }
            }
        });
        tasks.sort((a: any, b: any) => this.sortFunction(a, b));
        this.props.r.setInReduxState({ tasks, enableNewUnitMission, alerts });
    }

    protected handleMissionStatusGroup(missionStatusGroup: MissionStatusGroup) {
        this.props.r.setInReduxState({ missionStatusGroup });
    }

    protected handleTemporaryStrorageGroup(temporaryStorageGroup: TemporaryStorageGroup) {
        this.props.r.setInReduxState({ temporaryStorageGroup });
    }

    private createMissionParam(selectedTasks: any[]) {
        return {
            missionUid: this.props.s.missionStatusGroup === MissionStatusGroup.EXISTING && this.props.s.mission ? GanttUtils.toEntityUid("Mission2", this.props.s.mission.id) : null,
            humanResourceUid: this.props.s.humanResource ? GanttUtils.toEntityUid("HumanResource", this.props.s.humanResource.id) : null,
            equipmentResourceUid: this.props.s.equipmentResource ? GanttUtils.toEntityUid("EquipmentResource", this.props.s.equipmentResource.id) : null,
            objectUids: selectedTasks.map(task => GanttUtils.toEntityUid("Task", task.id)),
            // formerMissionUid
            // entireFlight: false, was removed from MissionService_AssignObjectToMission.Params
            // preObjectActions // delivery
            // objectToSave
            // objectToSaveAndAssign
            pendingMissionsNumber: this.props.s.numberOfMissionsToBeCreated,
            temporaryStorageAddressName: this.props.s.temporaryStorage ? GanttUtils.toEntityUid("Address", this.props.s.temporaryStorage.id) : null,
            equipmentTypeUid: this.props.s.equipmentResource?.equipmentType ? GanttUtils.toEntityUid("EquipmentType", this.props.s.equipmentResource.equipmentType.id) : null,
            comment: this.props.s.comment,
            createTruckObjectsForFlightUid: this.props.createTruckObjectsForFlightUid,
            // objectQuantitites, // list of modified quantity values(only for delivery)
            degradedMode: this.props.degradedMode,
            creationMode: null,
            assignToUnit: this.props.s.missionStatusGroup === MissionStatusGroup.NEW_UNIT,
            attachmentUid: this.props.s.attachment ? GanttUtils.toEntityUid("EquipmentResource", this.props.s.attachment.id) : null,
            forcedMissionStartDate: null,
            forcedMissionEndDate: null,
            missionCreationDate: this.props.missionCreationDate ? new Date(this.props.missionCreationDate) : null,
            doNotCreateEventOnMissionSave: false,
            assignWithNoEquipment: (AppMetaTempGlobals.appMetaInstance.helperAppContainer.dispatchers.getState().appContainerContextValue.initializationsForClient as XopsInitializationsForClient).mission2Settings.allowNoEquipment ?? false
        }
    }

    protected handleOk = async () => {
        let noAddress = false;
        const msgNoAddress: string[] = [];
        let baggageExceeded = false;
        const msgBaggageExceeded: string[] = [];
        let incompatibilityMissionType = false;
        let mType: any;
        this.props.s.tasks.forEach(task => {
            if (this.props.s.settings?.checkNullOilGroup && task.taskGroup && !task.taskGroup.oilGroup) {
                Utils.showGlobalAlert({ message: _msg("assignObjects.noOilgroup"), title: _msg("assignObjects.error.cannotaddTitle"), severity: Severity.ERROR });
                return;
            }
            if (!task.startAddress || !task.endAddress ||
                (this.props.s.settings?.checkAddressesFields && (task.startAddresses === undefined || !Object.keys(task.startAddresses).length || task.endAddresses === undefined || !Object.keys(task.endAddresses).length))) {
                noAddress = true;
                msgNoAddress.push((msgNoAddress.length + 1) + ". " + (task.taskType ? task.taskType.name : "") + " : " + task.name);
            }
            if (this.baggagesAlertMapMaxPrefixLength != -1) {
                for (let i = this.baggagesAlertMapMaxPrefixLength; i >= this.baggagesAlertMapMinPrefixLength; i--) {
                    const keyNoDest = task.name.substr(0, i) + "-";
                    const keyWithDest = keyNoDest + task.taskGroup.destination;
                    const baggagesLimitControled = JSON.parse(task.dataString)["baggagesLimitControled"];
                    if (this.baggagesAlertMap[keyNoDest] != null) {
                        if (this.baggagesAlertMap[keyNoDest] < task.processedBaggages && !baggagesLimitControled) {
                            baggageExceeded = true;
                            msgBaggageExceeded.push((msgBaggageExceeded.length + 1) + ". " + _msg("assignObjects.baggageNumberExceeded", task.name));
                        }
                        break;
                    } else if (this.baggagesAlertMap[keyWithDest] != null) {
                        if (this.baggagesAlertMap[keyWithDest] < task.processedBaggages && baggagesLimitControled) {
                            baggageExceeded = true;
                            msgBaggageExceeded.push((msgBaggageExceeded.length + 1) + ". " + _msg("assignObjects.baggageNumberExceeded", task.name));
                        }
                        break;
                    }
                }
            }
            if (!mType) {
                mType = task.missionType?.id;
            } else if (mType != task.missionType?.id) {
                incompatibilityMissionType = true;
            }
        });
        if (noAddress) {
            Utils.showGlobalAlert({ message: _msg("assignObjects.nullAddressObjects", msgNoAddress.join("\n")), title: _msg("assignObjects.error.cannotaddTitle"), severity: Severity.ERROR });
            return;
        }
        if (!this.props.s.tasks.length && !this.props.s.temporaryStorage) {
            Utils.showGlobalAlert({ message: _msg("assignObjects.error.cannotaddMessage"), title: _msg("assignObjects.error.cannotaddTitle"), severity: Severity.ERROR });
            return;
        }
        if (this.props.s.settings && this.props.s.settings.maxTasksForMission && (this.props.s.tasks.length > this.props.s.settings.maxTasksForMission || (this.props.s.missionStatusGroup === MissionStatusGroup.EXISTING && this.props.s.mission && Object.keys(this.props.s.mission?.tasks).length + this.props.s.tasks.length > this.props.s.settings.maxTasksForMission))) {
            Utils.showGlobalAlert({ message: _msg("mission2Service.fullEquipment", this.props.s.settings.maxTasksForMission), title: _msg("assignObjects.error.cannotaddTitle"), severity: Severity.ERROR });
            return;
        }
        if (baggageExceeded) {
            Utils.showGlobalAlert({ message: msgBaggageExceeded.join("\n"), title: _msg("assignObjects.error.cannotaddTitle"), severity: Severity.ERROR });
            return;
        }
        if (incompatibilityMissionType) {
            Utils.showGlobalAlert({ message: _msg("assignObjects.error.incompatibilityMissionType"), title: _msg("assignObjects.error.cannotaddTitle"), severity: Severity.ERROR });
            return;
        }

        try {
            let res: any[] = (await apolloClient.mutate({
                mutation: MISSION_ASSIGN_OBJECT_TO_MISSION, variables: {
                    params: this.createMissionParam(this.props.s.tasks)
                }
            })).data["mission2Service_assignObjectToMission"] as [];

            if (!res?.length) {
                Utils.showGlobalAlert({ message: _msg("assignObjects.error.cannotaddMessage"), title: _msg("assignObjects.error.cannotaddTitle"), severity: Severity.ERROR });
                return;
            } else {
                const savedMission = (await this.loadEntities("Mission2", filterById([GanttUtils.fromEntityUid(res[0]).id])))[0];
                // Need to add the saved mission in state in order to have it in OK/CANCEL handlers from the validation modal.
                this.props.r.setInReduxState({ savedMission });
                this.missionValidationRef.current!.checkMissionValidation({
                    missionEntityUid: GanttUtils.toEntityUid("Mission2", savedMission.id),
                    humanResourceEntityUid: this.props.s.humanResource ? GanttUtils.toEntityUid("HumanResource", this.props.s.humanResource.id) : null,
                    equipmentResourceEntityUid: this.props.s.equipmentResource ? GanttUtils.toEntityUid("EquipmentResource", this.props.s.equipmentResource.id) : null,
                });
            }
        } catch (e) {
            this.props.onError && this.props.onError();
        }
    }

    protected renderAlert() {
        return <div className="flex-container">
            {this.props.s.alerts.map((alert: any) => <div className="AssignTasksToResourcePage_alert">{alert}</div>)}
            {!this.props.s.tasks?.length && <div className="AssignTasksToResourcePage_alert">{_msg("assignObjects.noObjects")}</div>}
        </div>
    }

    protected renderTask(): React.ReactNode {
        const fdFlight = new FieldDescriptor();
        fdFlight.name = "flight";
        fdFlight.type = "Flight";

        const fdTask = new FieldDescriptor();
        fdTask.name = "tasks";
        fdTask.type = "Task";
        fdTask.enabled = this.props.s.flights.length != 0;

        return <Segment className="flex-container wh100 less-padding no-margin">
            <div className="flex-container gap10 less-padding">
                <div className="flex-container gap3">
                    <label>{flightEntityDescriptor.getIcon()}{flightEntityDescriptor.getLabel()}</label>
                    <AssociationFieldEditor
                        isMulti={true}
                        fieldDescriptor={fdFlight}
                        filter={Filter.createComposed(FilterOperators.forComposedFilter.and, [Filter.create("date", FilterOperators.forDate.today), Filter.create("showFlightInGantt", FilterOperators.forBoolean.equals, "true")])}
                        selectedValue={this.props.s.flights}
                        additionalSearchFields={["oilGroup", "name", "rotationFlight", "tasks", "tasks.mission", "tasks.archived", "tasks.startAddress", "tasks.endAddress", "tasks.taskGroup", "tasks.taskGroup.tasks", "tasks.taskGroup.tasks.mission"]}
                        onChange={async flights => {
                            const allTasks = flights ? flights.reduce((tasks: any[], flight: any) => tasks.concat(flight.tasks ?? []), []) : [];
                            await this.props.r.setInReduxState({ flights, allTasks });
                            this.filterTasks();
                        }}
                    />
                </div>
                <div className={"flex-container-row gap5 " + (this.props.temporaryStorage && this.props.s.settings?.showTemporaryStorageInfo ? "" : "no-display")} style={{ width: "20%" }}>
                    <Radio label={_msg("assignObjects.toPsa")} value={TemporaryStorageGroup.TO}
                        checked={this.props.s.temporaryStorageGroup === TemporaryStorageGroup.TO}
                        onClick={() => this.handleTemporaryStrorageGroup(TemporaryStorageGroup.TO)}
                    />
                    <Radio label={_msg("assignObjects.fromPsa")} value={TemporaryStorageGroup.FROM}
                        checked={this.props.s.temporaryStorageGroup === TemporaryStorageGroup.FROM}
                        onClick={() => this.handleTemporaryStrorageGroup(TemporaryStorageGroup.FROM)}
                    />
                </div>
                <div className="flex-container gap3">
                    <label>{taskEntityDescriptor.getIcon()}{taskEntityDescriptor.getLabel()}</label>
                    <AssociationFieldEditor
                        isMulti={true}
                        fieldDescriptor={fdTask}
                        selectedValue={this.props.s.tasks}
                        additionalSearchFields={["startAddress", "endAddress", "taskGroup.name", "taskGroup.oilGroup", "taskGroup.airline", "taskGroup.date", "taskGroup.number", "taskGroup.destination"]}
                        filter={Filter.createComposed(FilterOperators.forComposedFilter.and, [Filter.create('mission', FilterOperators.forEntityManyToOne.isEmpty), Filter.create("taskGroup.id", FilterOperators.forNumber.in, this.props.s.flights.map(flight => flight.id).join(","))])}
                        onChange={tasks => {
                            this.props.r.setInReduxState({ tasks });
                        }}
                    />
                </div>
                {this.renderAlert()}
                <div className={"flex-container-row flex-center " + ((!this.props.s.temporaryStorage || (this.props.s.temporaryStorage && this.props.s.missionStatusGroup == MissionStatusGroup.EXISTING)) ? "no-display" : "")}>
                    <div>{_msg("assignObjects.numberOfMissionsToBeCreated")}</div>
                    <Input defaultValue={1} value={this.props.s.numberOfMissionsToBeCreated} type="number" min={0} max={9} onChange={(e, data) => this.props.r.setInReduxState({ numberOfMissionsToBeCreated: Number(data.value) })}>
                    </Input>
                </div>
            </div>
        </Segment>
    }

    protected renderCapacity() {
        const usedCapacity: { [key: string]: number } = {};
        this.props.s.allTasks?.forEach(task => {
            if (!usedCapacity[task.taskType.name]) {
                usedCapacity[task.taskType.name] = 0;
            }
            usedCapacity[task.taskType.name] += task.quantity;
        });
        if (this.props.s.missionStatusGroup === MissionStatusGroup.EXISTING) {
            this.props.s.mission?.stops && Object.keys(this.props.s.mission.stops).forEach(key => {
                Object.keys(this.props.s.mission.stops[key].objectActions).forEach(key2 => {
                    const objectAction = this.props.s.mission.stops[key].objectActions[key2];
                    const data = JSON.parse(objectAction.dataString).map;
                    if (!usedCapacity[data.type]) {
                        usedCapacity[data.type] = 0;
                    }
                    if (objectAction.type === "load") {
                        usedCapacity[data.type] = data.quantity;
                    }
                })
            })
        }
        return <Segment className={"flex-container no-margin " + (this.props.s.settings?.showEquipmentTypeCapacityInfo ? "" : "no-display")} style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold" }}>{_msg("taskAssign.capacity")} </div>
            {this.props.s.equipmentTypeTasks.map((equipmentTypeTask: any) => {
                return <div style={{ whiteSpace: "nowrap" }}>
                    {equipmentTypeTask.taskType.name + " " + (equipmentTypeTask.quantity - (usedCapacity[equipmentTypeTask.taskType.name] ?? 0)) + " / " + equipmentTypeTask.quantity + "\n"}
                </div>
            })}
        </Segment>;
    }

    protected renderTemporaryStorage() {
        const fd = new FieldDescriptor();
        fd.name = "temporaryStorage";
        fd.type = "Address";
        return <> {
            this.props.s.settings?.showTemporaryStorageInfo && <div className="flex-container-row" style={{ opacity: !this.props.temporaryStorage ? 0.5 : 1, pointerEvents: !this.props.temporaryStorage ? "none" : undefined }} >
                <div className="flex-shrink-0">{_msg("flight.temporaryStorage") + ": "}</div>
                <AssociationFieldEditor selectedValue={this.props.s.temporaryStorage} fieldDescriptor={fd}
                    onChange={(temporaryStorage: any) => this.props.r.setInReduxState({ temporaryStorage: temporaryStorage })} />
            </div>
        } </>;
    }

    protected renderForm() {
        const fdEquipmentType = new FieldDescriptor();
        fdEquipmentType.name = "equipmentType";
        fdEquipmentType.type = "EquipmentType";

        const fdEquipmentResource = new FieldDescriptor();
        fdEquipmentResource.name = "equipmentResource";
        fdEquipmentResource.type = "EquipmentResource";

        const fdHr = new FieldDescriptor();
        fdHr.name = "hrName";
        fdHr.type = "HumanResource";

        const fdAttachment = new FieldDescriptor();
        fdAttachment.name = "attachment";
        fdAttachment.type = "EquipmentResource";

        return (<Segment className="flex-container-row wh100 less-padding no-margin" style={{ flex: 3 }}>
            <Form className="w100 less-padding">
                <FormField>
                    <label>{_msg("HumanResource.label")}</label>
                    <AssociationFieldEditor
                        isDisabled={this.props.humanResourceId != null}
                        selectedValue={this.props.s.humanResource}
                        fieldDescriptor={fdHr}
                    />
                </FormField>
                {this.props.s.settings?.showEquipmentResourceInfo ?
                    <FormField>
                        <label>{_msg("equipmentTypeTask.equipmentType")}</label>
                        <AssociationFieldEditor
                            isDisabled={this.props.s.equipmentResource}
                            selectedValue={this.props.s.equipmentType}
                            fieldDescriptor={fdEquipmentType}
                            onChange={(equipmentType: any) => this.props.r.setInReduxState({ equipmentType })}
                        />
                    </FormField> : null}
                {this.props.s.settings?.showEquipmentResourceInfo ?
                    <FormField>
                        <label>{_msg("mission.equipmentResource")}</label>
                        <AssociationFieldEditor
                            isDisabled={this.props.equipmentResourceId != null && this.props.humanResourceId == null}
                            selectedValue={this.props.s.equipmentResource}
                            fieldDescriptor={fdEquipmentResource}
                            onChange={(equipmentResource: any) => {
                                if (equipmentResource) {
                                    this.getEquipmentResource(equipmentResource.id);
                                } else {
                                    this.props.r.setInReduxState({ equipmentResource });
                                }
                            }}
                            filter={this.props.s.equipmentType ?
                                Filter.createComposed(FilterOperators.forComposedFilter.and, [Filter.create("equipmentType.id", FilterOperators.forNumber.equals, this.props.s.equipmentType.id), Filter.create("available", FilterOperators.forBoolean.equals, "TRUE")]) : undefined} />
                    </FormField> : null}
                <FormField>
                    <label>{_msg("equipmentResource.attachment")}</label>
                    <AssociationFieldEditor
                        selectedValue={this.props.s.attachment}
                        fieldDescriptor={fdAttachment}
                        onChange={(attachment: any) => this.props.r.setInReduxState({ attachment })}
                        filter={Filter.createComposed(FilterOperators.forComposedFilter.and, [Filter.create("available", FilterOperators.forBoolean.equals, "TRUE"), Filter.create("equipmentType.isAttachable", FilterOperators.forBoolean.equals, "TRUE")])} />
                </FormField>
                <FormField>
                    <label>{_msg("mission.comment")}</label>
                    <TextArea rows={2} className="flex-grow" value={this.props.s.comment} onChange={(_, data) => {
                        this.props.r.setInReduxState({ comment: data.value as string | undefined });
                    }} />
                </FormField>
            </Form></Segment>
        );
    }

    protected renderMissionStatusGroup() {
        return <Segment className="flex-container-row gap5 no-margin flex-center justify-content-space-between">
            <Radio label={_msg("assignObjects.newMission")} value={MissionStatusGroup.NEW}
                checked={this.props.s.missionStatusGroup === MissionStatusGroup.NEW}
                onClick={() => this.handleMissionStatusGroup(MissionStatusGroup.NEW)} />
            <Radio label={_msg("assignObjects.existingMission")} value={MissionStatusGroup.EXISTING}
                disabled={(this.props.s.missionStatusGroup != MissionStatusGroup.EXISTING && !this.props.s.equipmentResource && this.props.s.equipmentTypeTasks.filter((euipmentTypeTask: any) => euipmentTypeTask.taskType.ocupiedPositions > 0).length && (!this.props.s.mission || this.props.s.mission.status === "finished")) ? true : false}
                checked={this.props.s.missionStatusGroup === MissionStatusGroup.EXISTING}
                onClick={() => this.handleMissionStatusGroup(MissionStatusGroup.EXISTING)} />
            {this.props.s.settings?.showAssignUnitInfo && this.props.taskIds && _.isEqual(this.props.taskIds, this.props.initialSelectedTaskIds) &&
                <Radio label={_msg("assignObjects.newUnitMission")} value={MissionStatusGroup.NEW_UNIT}
                    disabled={this.props.s.humanResource?.unitId && this.props.s.enableNewUnitMission ? false : true}
                    checked={this.props.s.missionStatusGroup === MissionStatusGroup.NEW_UNIT}
                    onClick={() => this.handleMissionStatusGroup(MissionStatusGroup.NEW_UNIT)} />}
        </Segment>
    }

    protected getSteps(): WizardStep[] {
        const steps: WizardStep[] = this.props.hideTaskSelection ? [] : [{
            title: _msg("AssignTasksToResourcePage.selectTasks.label"),
            icon: "tasks",
            render: () => this.renderTask(),
            nextDisabled: () => this.props.s.tasks?.length == 0
        }];
        steps.push({
            title: _msg("AssignTasksToResourcePage.selectResource.label"),
            icon: "info",
            render: () => {
                return <>
                    {this.renderMissionStatusGroup()}
                    <div className="flex-container-row wh100 gap5">
                        {this.renderForm()}
                        {this.renderCapacity()}
                    </div>
                </>
            }
        })
        return steps;
    }

    protected async onMissionValidationFailed(mission: any) {
        if (this.props.s.tasks.length != mission.tasks.length) {
            await Promise.all(mission.tasks.map((object: any) =>
                apolloClient.mutate({ mutation: OAG_DELETE, variables: { id: object.id } })
            ));
        } else {
            (await apolloClient.mutate({ mutation: MISSION_DELETE, variables: { id: mission.id } }));
        }
        this.props.r.clearSavedMission();
        this.props.onCancel?.();
    }

    protected async onMissionValidationPassed(savedMission: any) {
        if (this.props.s.humanResource) {
            const missions = await this.loadEntities("Mission2", Filter.create("humanResourceId", FilterOperators.forNumber.equals, this.props.s.humanResource.id))
            for (const mission of missions) {
                if (savedMission.startTime.time >= mission.startTime.time && savedMission.startTime.time <= mission.endTime.time && mission.inactivityType != null && !mission.inactivityType.isPartial && !mission.inactivityType.hideInGantt) {
                    // change the inactivity endTime with the new mission startTime
                    (await apolloClient.mutate({ mutation: MISSION_SAVE, variables: { params: { endTime: savedMission.startTime } } }));
                    await this.getMission();
                    Utils.showGlobalAlert({ message: _msg("inactivity.stopInactivity"), title: _msg("global.confirmation"), severity: Severity.ERROR });
                    break;
                }
            }
        }
        if (savedMission && savedMission.humanResource && savedMission.equipmentResource) {
            // print mission only for vea and when RealTimeGanttSettings.degradedMode is true
            // Xops4Utils.printMission(savedMission.id, Boolean(Xops4Plugin.instance.missionGanttSheetOptionsManager.getOption(Xops4Constants.OPT_DEGRADED_MODE)));
        }
        this.afterMissionValidation(savedMission);
        this.props.r.clearSavedMission();
    }

    protected afterMissionValidation(savedMission: any) {
        if (savedMission && this.props.onAssign) {
            this.props.onAssign(GanttUtils.toEntityUid("Mission2", savedMission.id));
        }
    }

    render() {
        return <div className="AssignTaskToResourcePage flex-container gap5 wh100 small-padding">
            <WizardRRC id="AssignTaskToResourcePageWizard"
                steps={this.getSteps()}
                onFinishClick={this.handleOk}
                onCancelClick={this.props.onCancel}
            />
            <MissionValidationRRC id="MissionValidationAssignTasksToResourcePageRRC" ref={this.missionValidationRef}
                validationPassedHandler={() => {
                    this.afterMissionValidation(this.props.s.savedMission);
                    this.props.r.clearSavedMission();
                }}
                okHandler={() => this.onMissionValidationPassed(this.props.s.savedMission)}
                cancelHandler={() => this.onMissionValidationFailed(this.props.s.savedMission)} />
        </div>
    }
}

export const AssignTasksToResourcePageRRC = ReduxReusableComponents.connectRRC(AssignTasksToResourcePageState, AssignTasksToResourcePageReducers, AssignTasksToResourcePage);

export const assignTasksToResourcePageUrl = "/assignTasksToResource";
export const assignTasksToResourcePageRoute = () =>
    <PrivateRoute key="assignTasksToResourcePage"
        path={assignTasksToResourcePageUrl}
        render={(props) => {
            return <AssignTasksToResourcePageRRC id="assignTasksToResource" />
        }} />

export const assignTasksToResourcePageMenuEntry = () => {
    return {
        id: "assignTasksToResourcePage",
        content: _msg("AssignTasksToResourcePage.title"),
        to: assignTasksToResourcePageUrl, exact: true,
    }
};

// onAssign, onError, onCancel - TODO: add callbacks from flex
export const assignTasksToResourcePageFlexUrl = "/assignTasksToResourceFlex";
export const assignTasksToResourcePageFlexRoute = () =>
    <PrivateRoute key="assignTasksToResourcePageFlex"
        path={assignTasksToResourcePageFlexUrl}
        render={(props) => {
            let params = {} as any;
            for (const [key, value] of new URLSearchParams(props.location?.search).entries()) {
                if (key === "taskIds" || key === "initialSelectedTaskIds") {
                    params[key] = value.split(",");
                } else {
                    params[key] = value;
                }
            }
            return <AssignTasksToResourcePageRRC id="assignTasksToResourceFlex" {...params} />
        }} />
